<template>
  <div class="game-launch-overlay">
    <div class="launch-content">
      <!-- Loading State -->
      <template
        v-if="
          status === 'connecting' ||
            status === 'loading' ||
            status === 'redirecting'
        "
      >
        <div class="loading-spinner">
          <svg class="spinner-svg" viewBox="0 0 36 36">
            <defs>
              <linearGradient
                id="gameLoadingGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop offset="0%" style="stop-color:#8b0000;stop-opacity:1" />
                <stop offset="25%" style="stop-color:#dc2626;stop-opacity:1" />
                <stop offset="50%" style="stop-color:#ff4500;stop-opacity:1" />
                <stop offset="75%" style="stop-color:#ff6b00;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#ffa500;stop-opacity:1" />
              </linearGradient>
            </defs>
            <path
              class="spinner-bg"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              class="spinner-fill"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <div class="spinner-center">
            <div class="fire-emoji">🎰</div>
          </div>
        </div>

        <div class="loading-text">
          <div class="loading-title">{{ statusText }}</div>
          <div class="loading-subtitle">กรุณารอสักครู่</div>
        </div>

        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </template>

      <!-- Mock Credit State -->
      <template v-else-if="status === 'mock_credit'">
        <div class="mock-credit-icon">🎁</div>
        <div class="mock-credit-text">
          <div class="mock-credit-title">รบกวนยืนยันบัญชีกับแอดมิน</div>
          <div class="mock-credit-message">
            เครดิตฟรีอยู่ในกระเป๋าเรียบร้อยแล้ว<br />เหลือเพียงแค่แจ้งเบอร์โทรยืนยันบัญชีค่าา<br />
            กรุณาติดต่อแอดมินเพื่อทำการยืนยัน
          </div>
        </div>
        <div class="mock-credit-actions">
          <button class="btn-deposit" @click="handleContactAdmin">
            <span class="btn-icon">💬</span>
            ติดต่อแอดมิน
          </button>
          <button class="btn-back" @click="goToHomePage">
            <span class="btn-icon">←</span>
            กลับ
          </button>
        </div>
      </template>

      <!-- Error State -->
      <template v-else-if="status === 'error'">
        <div class="error-icon">⚠️</div>
        <div class="error-text">
          <div class="error-title">เกิดข้อผิดพลาด</div>
          <div class="error-message">{{ errorMessage }}</div>
        </div>
        <div class="error-actions">
          <button class="btn-retry" @click="retry">
            <span class="btn-icon">🔄</span>
            ลองใหม่อีกครั้ง
          </button>
          <button class="btn-back" @click="goBack">
            <span class="btn-icon">←</span>
            กลับ
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { isUserLoggedIn } from '@/auth/utils'

export default {
  name: 'GameplayLoginSlot',

  data() {
    return {
      status: 'connecting',
      statusText: 'กำลังเชื่อมต่อ...',
      errorMessage: '',
      timeoutMs: 15000,
      debug: false,
      abortController: null
    }
  },

  computed: {
    ...mapState('member', ['isMockCredit', 'contactUrl']),
    provider() {
      return this.$route.query.p || ''
    },
    gameId() {
      return this.$route.query.g || ''
    },
    tab() {
      return this.$route.query.t || ''
    },
    isValidParams() {
      return Boolean(this.provider && this.gameId)
    }
  },

  async mounted() {
    this.debug = this.$route.query.debug === '1'
    this.log('Component mounted', {
      provider: this.provider,
      gameId: this.gameId,
      tab: this.tab
    })
    await this.launchGame()
  },

  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort()
    }
  },

  methods: {
    log(message, data = null) {
      if (this.debug) {
        if (data) {
          console.log(`[GameLaunch] ${message}`, data)
        } else {
          console.log(`[GameLaunch] ${message}`)
        }
      }
    },

    checkAuth() {
      if (!isUserLoggedIn()) {
        this.log('User not authenticated, redirecting to login')
        this.$router.replace('/?showLogin=true')
        return false
      }
      return true
    },

    async checkRestriction() {
      try {
        const response = await this.$http.get('member/check-restrict', {
          params: {
            provider: this.provider,
            gameId: this.gameId,
            tab: this.tab
          }
        })
        return response.data
      } catch (error) {
        // On error, allow game launch (fail-open)
        return { success: false, onlySlot: false }
      }
    },

    async launchGame() {
      if (!this.checkAuth()) {
        return
      }

      // Check mock credit state - user needs to deposit first
      if (this.isMockCredit) {
        this.status = 'mock_credit'
        this.log('User has mock credit, showing deposit prompt')
        return
      }

      if (!this.isValidParams) {
        this.handleError(new Error('INVALID_PARAMS'))
        return
      }

      // Check slot-only restriction
      const restrictionData = await this.checkRestriction()
      if (restrictionData.success && restrictionData.onlySlot) {
        if (this.tab.toLowerCase() !== 'slot') {
          this.handleError(new Error('SLOT_ONLY_RESTRICTION'))
          return
        }
      }

      // Check promotion provider/game restriction (server verdict for this game)
      if (restrictionData.success && restrictionData.restriction?.blocked) {
        this.handleError(new Error('PROMO_GAME_RESTRICTION'))
        return
      }

      this.status = 'connecting'
      this.statusText = 'กำลังเชื่อมต่อ...'
      try {
        const uri = await this.fetchGameUrl()
        this.handleSuccess(uri)
      } catch (error) {
        this.handleError(error)
      }
    },

    async fetchGameUrl() {
      this.abortController = new AbortController()
      const timeoutId = setTimeout(() => {
        this.abortController.abort()
      }, this.timeoutMs)

      try {
        this.status = 'loading'
        this.statusText = 'กำลังโหลดเกม...'
        this.log('Fetching game URL')

        const response = await this.$http.post(
          'member/gameplay/login',
          {
            provider: this.provider,
            gameId: this.gameId,
            tab: this.tab
          },
          {
            signal: this.abortController.signal
          }
        )

        clearTimeout(timeoutId)
        this.log('API response received', response.data)

        // Server hard-block: promotion restricts this game (response has no `result`)
        if (response.data.code === 'PROMO_GAME_RESTRICTED') {
          throw new Error('PROMO_GAME_RESTRICTION')
        }

        if (response.data.result?.code === 0) {
          const uri = response.data.result.data?.uri
          if (!uri) {
            throw new Error('NO_URI')
          }
          return uri
        }

        const errorCode = response.data.result?.code
        const errorMsg = response.data.result?.message || 'Unknown error'
        throw new Error(`API_ERROR:${errorCode}:${errorMsg}`)
      } catch (error) {
        clearTimeout(timeoutId)
        throw error
      }
    },

    handleSuccess(uri) {
      this.status = 'redirecting'
      this.statusText = 'กำลังเปิดเกม...'
      this.log('Redirecting to game', { uri })

      setTimeout(() => {
        window.location.assign(uri)
      }, 100)
    },

    handleError(error) {
      this.status = 'error'
      this.log('Error occurred', { error: error.message })

      if (error.name === 'AbortError' || error.message === 'canceled') {
        this.errorMessage = 'การเชื่อมต่อหมดเวลา กรุณาลองใหม่อีกครั้ง'
        return
      }

      if (error.message === 'INVALID_PARAMS') {
        this.errorMessage = 'ข้อมูลเกมไม่ถูกต้อง'
        return
      }

      if (error.message === 'NO_URI') {
        this.errorMessage = 'ไม่สามารถเปิดเกมได้ในขณะนี้'
        return
      }

      if (error.message === 'SLOT_ONLY_RESTRICTION') {
        this.errorMessage =
          'ลูกค้ามีการรับโปรโมชั่นที่จำกัดการเล่นแต่สล็อต จึงไม่สามารถเข้าเกมส์นี้ได้'
        return
      }

      if (error.message === 'PROMO_GAME_RESTRICTION') {
        this.errorMessage =
          'โปรโมชั่นที่คุณรับอยู่ไม่อนุญาตให้เล่นเกมนี้ กรุณาทำเทิร์นให้ครบหรือติดต่อแอดมิน'
        return
      }

      if (error.message?.startsWith('API_ERROR:')) {
        const parts = error.message.split(':')
        const code = parts[1]
        if (code === '401' || code === '-1') {
          this.errorMessage = 'กรุณาเข้าสู่ระบบก่อนเล่นเกม'
        } else {
          this.errorMessage = 'ไม่สามารถเข้าสู่เกมได้ชั่วคราว'
        }
        return
      }

      if (error.response?.status === 401) {
        this.errorMessage = 'กรุณาเข้าสู่ระบบก่อนเล่นเกม'
        return
      }

      this.errorMessage = 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง'
    },

    retry() {
      this.log('Retrying game launch')
      this.errorMessage = ''
      this.launchGame()
    },

    goBack() {
      this.log('Navigating back')
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/')
      }
    },

    handleContactAdmin() {
      if (this.contactUrl) {
        window.open(this.contactUrl, '_blank')
      }
    },

    goToHomePage() {
      this.log('Navigating to Homepage')
      this.$router.push('/')
    }
  }
}
</script>

<style lang="scss" scoped>
.game-launch-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.launch-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  text-align: center;
  padding: 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(212, 175, 55, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  min-width: 300px;
  max-width: 90vw;

  @media (max-width: 768px) {
    padding: 30px 20px;
    gap: 24px;
    min-width: 280px;
  }
}

// Loading Spinner
.loading-spinner {
  position: relative;
  width: 100px;
  height: 100px;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
}

.spinner-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  animation: spin 2s linear infinite;
}

.spinner-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 2;
}

.spinner-fill {
  fill: none;
  stroke: url(#gameLoadingGradient);
  stroke-width: 2.5;
  stroke-dasharray: 75, 100;
  stroke-linecap: round;
  animation: progress 1.5s ease-in-out infinite;
}

.spinner-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.fire-emoji {
  font-size: 32px;
  animation: pulse 1.5s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 26px;
  }
}

// Loading Text
.loading-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.loading-title {
  color: #fff;
  font-size: 20px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 18px;
  }
}

.loading-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 400;
}

// Loading Dots
.loading-dots {
  display: flex;
  gap: 10px;
  justify-content: center;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: linear-gradient(45deg, #d4af37, #ffa500);
    animation: dots 1.4s ease-in-out infinite both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
    &:nth-child(3) {
      animation-delay: 0s;
    }
  }
}

// Error State
.error-icon {
  font-size: 64px;
  animation: shake 0.5s ease-in-out;

  @media (max-width: 768px) {
    font-size: 48px;
  }
}

.error-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error-title {
  color: #ff6b6b;
  font-size: 22px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 20px;
  }
}

.error-message {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.5;
  max-width: 300px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.error-actions {
  display: flex;
  gap: 16px;
  margin-top: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
}

.btn-retry,
.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
}

.btn-retry {
  background: linear-gradient(45deg, #d4af37, #ffa500);
  color: #1a1a2e;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
}

.btn-back {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
  }
}

.btn-icon {
  font-size: 18px;
}

// Animations
@keyframes spin {
  from {
    transform: rotate(-90deg);
  }
  to {
    transform: rotate(270deg);
  }
}

@keyframes progress {
  0% {
    stroke-dasharray: 0, 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 75, 100;
    stroke-dashoffset: -15;
  }
  100% {
    stroke-dasharray: 75, 100;
    stroke-dashoffset: -90;
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes dots {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-5px);
  }
  40%,
  80% {
    transform: translateX(5px);
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

// Mock Credit State
.mock-credit-icon {
  font-size: 64px;
  animation: bounce 1s ease-in-out infinite;

  @media (max-width: 768px) {
    font-size: 48px;
  }
}

.mock-credit-text {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mock-credit-title {
  color: #ffa500;
  font-size: 22px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 20px;
  }
}

.mock-credit-message {
  color: rgba(255, 255, 255, 0.8);
  font-size: 16px;
  line-height: 1.6;
  max-width: 300px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
}

.mock-credit-actions {
  display: flex;
  gap: 16px;
  margin-top: 10px;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
  }
}

.btn-deposit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: linear-gradient(45deg, #d4af37, #ffa500);
  color: #1a1a2e;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    width: 100%;
  }
}
</style>
